import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSymbolQuote } from './hooks/useSymbolQuote';
import { useDefaultAccount, indexTileFromBars } from './hooks/useDefaultAccount';
// 2026-06-06 h-source-of-truth-purge — header + dashboard tab
// now consume the canonical /user/portfolio account via
// useAccountSummary; defaultAccount is only used for the
// legacy operator views that have always supplied it.
import useAccountSummary from './hooks/useAccountSummary';
import { useViewerPortfolio } from './hooks/useViewerPortfolio';
import { useAuthenticatedUser, useUserDisplay } from './hooks/useAuthenticatedUser';
import { useAccountIntel, useMarketsOverview } from './hooks/useAccountIntel';
// S27B: live supervised-paper readiness for the dashboard banner.
import {
  useSupervisedPaperReadiness,
  isSupervisedPaperEnabled,
} from './hooks/useSupervisedPaperReadiness';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import MarketPulseModule, { MarketPulseDashboardContextCard, MarketPulseTickerStrip } from './components/MarketPulseModule';
import { MarketPulseTicker } from './components/MarketPulseTicker';
import PaperSessionPlanDrawer from './components/PaperSessionPlanDrawer';
import MarketPulsePage from './components/MarketPulse';
// phase-fallen-edge-ui-v1 / phase-short-intel-ui-v1 — standalone routes for
// the advisory-only loser/short intelligence surfaces. Panels themselves are
// also embedded into MarketPulse and StrategyIntelligenceHub.
import FallenEdgePanel from './components/FallenEdgePanel';
import ShortIntelligencePanel from './components/ShortIntelligencePanel';
import GlossaryModule from './components/GlossaryModule';
import OnboardingMockup from './components/OnboardingMockup';
// [phase11-v1] The Signal module (formerly The Matrix) — tradable gainer radar.
import SignalModule from './components/Signal/SignalModule';
import StrategyIntelligenceHub from './components/StrategyIntelligenceHub';
// [phase12cf-signal-icon-v1] Canonical Signal broadcast/radar icon.
import SignalIcon from './components/Signal/SignalIcon';
import { 
  Activity,
  CandlestickChart,
  Briefcase,
  ShieldAlert,
  GraduationCap,
  FlaskConical,
  Atom,
  SlidersHorizontal,
  Search, 
  Bell, 
  User, 
  ChevronRight, 
  ChevronLeft,
  ArrowUpRight, 
  ArrowDownRight,
  Zap,
  BarChart3,
  Cpu,
  Info,
  Maximize2,
  MoreHorizontal,
  Play,
  Clock,
  Layers,
  Terminal,
  ChevronDown,
  Menu,
  X,
  LogOut,
  UserCircle,
  Link2,
  Key,
  CreditCard,
  Brain,
  Lock,
  Sun,
  Moon,
  LayoutDashboard,
  TrendingUp,
  PieChart,
  ShieldCheck,
  BookOpen,
  Settings,
  Sparkles,
} from 'lucide-react';
import { OwlIcon, ElephantIcon, BrainIcon, CompassIcon } from './components/AIIcons';
import { motion, AnimatePresence } from 'motion/react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { cn, logInteraction } from './lib/utils';
import { GLOSSARY, ADVERSYN_COUNCIL, TRADING_STYLES } from './constants';

// --- Mock Data ---
const DATA_1D = [
  { time: '09:30', price: 420.50, volume: 1200 },
  { time: '10:00', price: 421.20, volume: 800 },
  { time: '10:30', price: 419.80, volume: 1500 },
  { time: '11:00', price: 422.10, volume: 2100 },
  { time: '11:30', price: 423.40, volume: 1100 },
  { time: '12:00', price: 422.90, volume: 900 },
  { time: '12:30', price: 424.15, volume: 1300 },
  { time: '13:00', price: 425.60, volume: 1800 },
  { time: '13:30', price: 424.80, volume: 1400 },
  { time: '14:00', price: 426.20, volume: 2500 },
  { time: '14:30', price: 427.50, volume: 3200 },
  { time: '15:00', price: 428.10, volume: 2800 },
  { time: '15:30', price: 427.90, volume: 2100 },
  { time: '16:00', price: 429.45, volume: 4500 },
];

const DATA_1W = [
  { time: 'Mon', price: 410.50, volume: 12000 },
  { time: 'Tue', price: 415.20, volume: 8000 },
  { time: 'Wed', price: 412.80, volume: 15000 },
  { time: 'Thu', price: 422.10, volume: 21000 },
  { time: 'Fri', price: 429.45, volume: 11000 },
];

const DATA_1M = [
  { time: 'Week 1', price: 390.50, volume: 52000 },
  { time: 'Week 2', price: 405.20, volume: 48000 },
  { time: 'Week 3', price: 418.80, volume: 65000 },
  { time: 'Week 4', price: 429.45, volume: 71000 },
];

const DATA_1Y = [
  { time: 'Jan', price: 320.50, volume: 120000 },
  { time: 'Feb', price: 335.20, volume: 80000 },
  { time: 'Mar', price: 352.80, volume: 150000 },
  { time: 'Apr', price: 362.10, volume: 210000 },
  { time: 'May', price: 383.40, volume: 110000 },
  { time: 'Jun', price: 392.90, volume: 90000 },
  { time: 'Jul', price: 404.15, volume: 130000 },
  { time: 'Aug', price: 415.60, volume: 180000 },
  { time: 'Sep', price: 404.80, volume: 140000 },
  { time: 'Oct', price: 416.20, volume: 250000 },
  { time: 'Nov', price: 427.50, volume: 320000 },
  { time: 'Dec', price: 429.45, volume: 280000 },
];

// 2026-06-06 g3-no-fake-data-outside-sim-lab — WATCHLIST must NOT
// carry hardcoded AAPL/TSLA/NVDA/MSFT/AMD literals. When user has no
// real positions, render the empty state — never the fake watchlist.
const WATCHLIST: Array<{ symbol: string; price: string; change: string; trend: string }> = [];

// AI_DEBATE constant removed (was hardcoded debate content). The dashboard
// renders an explicit "Council debate unavailable" card unless a real backend
// service is connected.
const AI_DEBATE: Array<{ role: string; content: string; sentiment: 'bullish' | 'bearish' | 'neutral'; confidence: number }> = [];

// --- Components ---

const TutorIcon = ({ size, className }: { size: number, className?: string }) => (
  <div className={cn(
    "w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(16,185,129,0.6)] shrink-0",
    className
  )}>
    <Sparkles size={size} className="text-white fill-white/20 drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]" />
  </div>
);

const SidebarItem = ({ icon: Icon, label, active, onClick, collapsed, 'data-testid': testId }: { icon: any, label: string, active?: boolean, onClick?: () => void, collapsed?: boolean, 'data-testid'?: string }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={cn(
      "w-full flex items-center gap-3 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden",
      collapsed ? "justify-center px-0" : "px-4",
      active 
        ? "bg-emerald-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] border border-emerald-500/50" 
        : "text-[var(--muted-text)] hover:bg-[var(--input-bg)] hover:text-[var(--app-text)] border border-transparent"
    )}
    title={collapsed ? label : undefined}
  >
    {active && (
      <motion.div 
        layoutId="sidebar-active-glow"
        className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50"
        initial={false}
        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
      />
    )}
    <Icon size={20} className={cn("transition-all duration-300 shrink-0 relative z-10", active ? "drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" : "group-hover:scale-110")} />
    {!collapsed && <span className="font-black text-[10px] uppercase italic tracking-widest whitespace-nowrap relative z-10">{label}</span>}
  </button>
);

const Card = ({ children, title, icon: Icon, className, headerAction, testid }: { children: React.ReactNode, title?: string, icon?: any, className?: string, headerAction?: React.ReactNode, testid?: string }) => (
  <div data-testid={testid} className={cn("bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm transition-colors", className)}>
    {(title || Icon) && (
      <div className="px-5 py-4 border-b border-[var(--card-header-border)] flex items-center justify-between bg-[var(--card-header-bg)] transition-colors">
            <div className="flex items-center gap-2">
              <Brain size={18} className="text-sky-500" />
              <h3 className="text-xs font-black text-[var(--app-text)] text-xs uppercase tracking-widest font-display italic">{title}</h3>
            </div>
        {headerAction}
      </div>
    )}
    <div className="p-5">
      {children}
    </div>
  </div>
);

// UI-CLEANUP 2026-06-12 — ExecutionBlockedBanner (the large supervised-paper /
// execution-blocked Dashboard banner) was removed: redundant with the compact
// header chip. Status remains on the header chip + Risk Center; the Operations
// Command Center retains the full paper-study-warnings / live-trading-blockers
// view. Backend readiness logic is unchanged; the readiness hook is still used
// by the header execution-mode chip. (2026-06-13: the redundant Dashboard
// TraderObservationWorkflow status panel + the gated demo-mode banner were removed.)

const Stat = ({ label, value, change, trend, sparkData, onClick }: { label: string, value: string, change?: string, trend?: 'up' | 'down', sparkData?: any[], onClick?: () => void }) => (
  <div 
    className="flex flex-col gap-3 cursor-pointer group/stat relative"
    onClick={onClick}
    title={`${label}: Open: ${value}, High: ${value}, Low: ${value}`}
  >
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest group-hover/stat:text-sky-500 transition-colors">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-black text-[var(--app-text)] italic tracking-tight">{value}</span>
        {change && (
          <span className={cn(
            "text-xs font-bold flex items-center gap-0.5",
            trend === 'up' ? "text-emerald-600" : "text-rose-600"
          )}>
            {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {change}
          </span>
        )}
      </div>
    </div>
    
    {sparkData && (
      <div className="h-10 w-full opacity-60 group-hover/stat:opacity-100 transition-opacity">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkData}>
            <defs>
              <linearGradient id={`gradient-${label.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={trend === 'up' ? '#10b981' : '#ef4444'} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={trend === 'up' ? '#10b981' : '#ef4444'} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={trend === 'up' ? '#10b981' : '#ef4444'} 
              strokeWidth={2} 
              fillOpacity={1} 
              fill={`url(#gradient-${label.replace(/\s+/g, '')})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

const IndexDetailPanel = ({ indexId, onBack }: { indexId: string, onBack: () => void }) => {
  // Real values pulled from /markets/overview tiles, with explicit unavailable when missing
  const { overview: _overview } = useMarketsOverview();
  const _byId: Record<string, string> = { sp500: 'SPY', nasdaq: 'QQQ', dow: 'DIA', vix: 'VIX', btc: 'BTC/USD' };
  const _wantSym = _byId[indexId] || indexId.toUpperCase();
  const _tile = (_overview?.tiles || []).find((t) => t.symbol === _wantSym);
  const _displayLabel: Record<string, string> = { sp500: 'S&P 500 (SPY proxy)', nasdaq: 'NASDAQ (QQQ proxy)', dow: 'Dow Jones (DIA proxy)', vix: 'VIX', btc: 'BTC / USD' };
  const _isProviderUnavailable = indexId === 'vix' && (!_tile || _tile.value == null);
  const indexInfo = _tile && _tile.value != null ? {
    name: _displayLabel[indexId] || _tile.display_label || _tile.symbol,
    value: _tile.value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
    change: _tile.change_pct != null ? `${_tile.change_pct >= 0 ? '+' : ''}${_tile.change_pct.toFixed(2)}%` : '—',
    trend: ((_tile.change_pct ?? 0) >= 0 ? 'up' : 'down') as 'up' | 'down',
  } : {
    name: _displayLabel[indexId] || indexId.toUpperCase(),
    value: '—',
    change: '—',
    trend: 'up' as 'up' | 'down',
  };

  // [phase12cf-market-index-graphs-v1] Real per-symbol per-timeframe chart wiring.
  //
  // Before this fix: the chart was hardcoded to DATA_1D for every selected
  // index, and the timeframe buttons sat outside the chart card with no
  // onClick. Now:
  //   * useSymbolQuote(symbol, tf, assetClass) fetches /quote/<sym>/bars
  //     for the selected index + timeframe.
  //   * 3M and 5Y currently fall back to 1Y data and surface a clear
  //     "approximated using 1Y data" subnote (no fabrication — we just
  //     reuse the longest supported timeframe).
  //   * The button row moves INSIDE the Card via headerAction.
  type DetailTf = '1D' | '1W' | '1M' | '3M' | '1Y' | '5Y';
  const detailTimeframes: DetailTf[] = ['1D', '1W', '1M', '3M', '1Y', '5Y'];
  const [selectedTf, setSelectedTf] = useState<DetailTf>('1D');
  const _quoteTf: '1D' | '1W' | '1M' | '1Y' =
    selectedTf === '3M' || selectedTf === '5Y' ? '1Y' :
    (selectedTf as '1D' | '1W' | '1M' | '1Y');
  const _approximated = selectedTf === '3M' || selectedTf === '5Y';
  const _assetClass: 'us_equity' | 'crypto' = indexId === 'btc' ? 'crypto' : 'us_equity';
  const _quoteResult = useSymbolQuote(_wantSym, _quoteTf, _assetClass);
  // Defensively read fields so the patch doesn't break if the hook
  // shape evolves (it currently returns { quote, loading, error }).
  const _quote = (_quoteResult as any)?.quote || null;
  const _chartLoading = Boolean((_quoteResult as any)?.loading);
  const _chartError = (_quoteResult as any)?.error || null;
  const _chartBars: Array<{ t: string; o: number; h: number; l: number; c: number; v: number }> =
    Array.isArray(_quote?.bars) ? _quote.bars : [];
  const _fmtAxis = (iso: string): string => {
    try {
      const d = new Date(iso);
      if (selectedTf === '1D') {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      if (selectedTf === '1W' || selectedTf === '1M') {
        return d.toLocaleDateString([], { month: 'short', day: '2-digit' });
      }
      return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
    } catch { return iso; }
  };
  const chartData = _chartBars.map((b) => ({
    time: _fmtAxis(b.t),
    price: b.c,
    symbol: _wantSym,
  }));
  const _chartUnavailable = !_chartLoading && chartData.length === 0;
  const _timeframeButtons = (
    <div
      className="flex items-center gap-1 bg-[var(--input-bg)] p-1 rounded-lg border border-[var(--input-border)]"
      data-testid="index-chart-timeframes"
    >
      {detailTimeframes.map((tf) => (
        <button
          key={tf}
          type="button"
          onClick={() => setSelectedTf(tf)}
          data-testid={`index-chart-tf-${tf}`}
          aria-pressed={selectedTf === tf}
          className={cn(
            "px-2.5 py-1 text-[10px] font-black uppercase italic rounded-md transition-all border",
            selectedTf === tf
              ? "bg-[var(--app-text)] text-[var(--card-bg)] border-[var(--app-text)]"
              : "text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent"
          )}
        >
          {tf}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-xl bg-[var(--card-bg)] border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">{indexInfo.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {_isProviderUnavailable ? (
                <span className="text-xs font-black italic uppercase tracking-widest text-[var(--muted-text)] px-2 py-1 rounded-md border border-[var(--card-border)] bg-[var(--input-bg)]">
                  Provider not connected
                </span>
              ) : (
                <>
                  <span className="text-xl font-bold text-[var(--app-text)]">{indexInfo.value}</span>
                  <span className={cn(
                    "text-sm font-black italic",
                    indexInfo.trend === 'up' ? "text-emerald-600" : "text-rose-600"
                  )}>
                    {indexInfo.change}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* [phase12cf-market-index-graphs-v1] Timeframe controls moved
            into the Full Index Performance Card's headerAction slot
            below — they used to float here on the far right of the
            outer header, disconnected from the chart they control. */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Card
            title={`Full Index Performance — ${indexInfo.name}`}
            icon={TrendingUp}
            headerAction={_timeframeButtons}
            className="index-chart-card"
          >
            {_approximated && (
              <div
                data-testid="index-chart-approximated-note"
                className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-600"
              >
                {selectedTf} approximated using 1Y data (3M and 5Y bars not provided by the upstream feed)
              </div>
            )}
            <div className="h-[280px] sm:h-[400px] w-full" data-testid={`index-chart-${indexId}`} data-symbol={_wantSym} data-timeframe={selectedTf}>
              {_chartLoading ? (
                <div className="flex h-full items-center justify-center text-[12px] font-bold uppercase tracking-widest text-[var(--muted-text)]" data-testid="index-chart-loading">
                  Loading {indexInfo.name} {selectedTf} chart…
                </div>
              ) : _chartUnavailable ? (
                <div
                  className="flex h-full flex-col items-center justify-center gap-2 text-center"
                  data-testid="index-chart-unavailable"
                >
                  <span className="text-[12px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                    Chart data is not available for {indexInfo.name} ({_wantSym}) at {selectedTf} right now.
                  </span>
                  {_chartError && (
                    <span className="text-[10px] text-[var(--muted-text)]">
                      Provider state: {String(_chartError).slice(0, 140)}
                    </span>
                  )}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorDetail" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--card-header-border)" vertical={false} />
                    <XAxis dataKey="time" stroke="var(--muted-text)" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--muted-text)" fontSize={10} tickLine={false} axisLine={false} domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px' }}
                      formatter={(value: number) => [
                        typeof value === 'number'
                          ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : value,
                        indexInfo.name,
                      ]}
                      labelFormatter={(label: string) => `${indexInfo.name} · ${label}`}
                    />
                    <Area type="monotone" dataKey="price" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorDetail)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-8">
          <Card title="Component Influence" icon={PieChart}>
            <div className="space-y-4">
              {[
                { name: 'Technology', value: 38, color: '#10b981' },
                { name: 'Financials', value: 15, color: '#059669' },
                { name: 'Healthcare', value: 12, color: '#f59e0b' },
                { name: 'Consumer', value: 10, color: '#ef4444' },
                { name: 'Industrials', value: 25, color: '#6366f1' },
              ].map(sector => (
                <div key={sector.name} className="space-y-1.5">
                  <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                    <span className="text-[var(--muted-text)]">{sector.name}</span>
                    <span className="text-[var(--app-text)]">{sector.value}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-[var(--input-bg)] rounded-full overflow-hidden">
                    <div className="h-full transition-all duration-1000" style={{ width: `${sector.value}%`, backgroundColor: sector.color }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Market Breadth" icon={Activity}>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-2xl bg-[var(--input-bg)] border border-[var(--input-border)] text-center">
                <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-1">Advancers</div>
                <div className="text-xl font-black text-emerald-600 italic">242</div>
              </div>
              <div className="p-4 rounded-2xl bg-[var(--input-bg)] border border-[var(--input-border)] text-center">
                <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-1">Decliners</div>
                <div className="text-xl font-black text-rose-600 italic">58</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const AdversynCouncil = ({ onAskCouncil }: { onAskCouncil: () => void }) => {
  const [activeModel, setActiveModel] = useState(0);

  return (
    <Card title="Council Strategy Debate" icon={Atom} className="overflow-visible h-auto">
      <div className="flex flex-wrap gap-2 mb-6">
        {ADVERSYN_COUNCIL.map((model, idx) => (
          <button
            key={model.id}
            onClick={() => setActiveModel(idx)}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase italic transition-all",
              activeModel === idx 
                ? "bg-emerald-600 text-white" 
                : "bg-[var(--input-bg)] text-[var(--muted-text)] hover:bg-[var(--input-border)]"
            )}
          >
            {model.name}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeModel}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center">
                {activeModel === 0 && <BrainIcon size={24} />}
                {activeModel === 1 && <OwlIcon size={24} />}
                {activeModel === 2 && <ElephantIcon size={24} />}
                {activeModel === 3 && <CompassIcon size={24} />}
              </div>
              <div>
                <h4 className="text-[var(--app-text)] font-bold uppercase italic text-sm">{ADVERSYN_COUNCIL[activeModel].name}</h4>
                <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{ADVERSYN_COUNCIL[activeModel].specialty}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1">Confidence</div>
              <div className="w-24 h-1.5 bg-[var(--input-bg)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-600" 
                  style={{ width: `${AI_DEBATE.length > 0 ? (AI_DEBATE[activeModel]?.confidence ?? 0) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>

          <div className="bg-[var(--input-bg)] rounded-xl p-4 border border-[var(--input-border)]">
            <p className="text-sm text-[var(--muted-text)] leading-relaxed italic">
              "{AI_DEBATE.length > 0 ? AI_DEBATE[activeModel]?.content : 'Council not yet engaged. Use "Ask the Council" below to start a live debate.'}"
            </p>
          </div>

          <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest">
            <span className={cn(
              "px-2 py-0.5 rounded font-black",
              (AI_DEBATE[activeModel]?.sentiment === 'bullish') ? "bg-emerald-500/10 text-emerald-600" :
              (AI_DEBATE[activeModel]?.sentiment === 'bearish') ? "bg-rose-500/10 text-rose-600" :
              "bg-[var(--input-bg)] text-[var(--muted-text)]"
            )}>
              {AI_DEBATE.length > 0 ? AI_DEBATE[activeModel]?.sentiment : 'unavailable'}
            </span>
            <span className="text-[var(--muted-text)] flex items-center gap-1">
              <Clock size={12} /> Just now
            </span>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 pt-6 border-t border-[var(--card-header-border)] space-y-3">
        <button
          onClick={onAskCouncil}
          data-testid="dashboard-btn-ask-council"
          className="w-full py-3 bg-[var(--app-text)] hover:opacity-90 text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic transition-all shadow-lg shadow-[var(--app-text)]/20 flex items-center justify-center gap-2"
        >
          <Atom size={16} />
          Ask the Council
        </button>
        <button
          onClick={onAskCouncil}
          data-testid="dashboard-btn-view-debate"
          className="w-full py-2.5 bg-[var(--input-bg)] border border-[var(--input-border)] hover:bg-[var(--input-border)] text-[var(--app-text)] rounded-xl text-[10px] font-black uppercase italic transition-all flex items-center justify-center gap-2"
        >
          View Full Debate
        </button>
      </div>
    </Card>
  );
};

import AICouncilAnalysisCenter from './components/AICouncilAnalysisCenter';
import RiskCenter from './components/RiskCenter';
import OMSModule from './components/OMSModule';
import AccountPortal from './components/AccountPortal';
import MarketsModule from './components/MarketsModule';
import PortfolioModule from './components/PortfolioModule';
import StrategyLabModule from './components/StrategyLabModule';
import SimulationLabModule from './components/SimulationLabModule';
import AcademyModule from './components/AcademyModule';
import AcademyLessonPage from './components/AcademyLessonPage';
import PageLegend from './components/PageLegend';
import TradingDisclaimerModal from './components/TradingDisclaimerModal';
import { AITutor } from './components/AITutor';
import AuthPage from './components/AuthPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import LandingPage from './components/LandingPage';
import SupportWidget from './components/SupportWidget';
import { useAuthStore } from './hooks/useAuthStore';
import { authStore } from './store/authStore';

import { NotificationCenter, Notification } from './components/NotificationCenter';
// ADV-ALERTS-SYMBOL-CLICK-CHART-001 — open a symbol's chart/detail from an alert ticker click.
import { openSymbolChart } from './lib/symbolChart';
// ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — alert timestamp + BUY-candidate audio chime.
import { toEpochMs, formatAlertTime } from './lib/alertTime';
import { playBuyChime, primeAudio } from './lib/alertSound';

import { apiV1Base } from './lib/apiConfig';
// --- Main App ---

const getPageTerms = (tab: string) => {
  switch (tab) {
    case 'dashboard':
      return GLOSSARY.filter(t => ['VWAP', 'Order Book', 'P&L', 'Slippage'].includes(t.term));
    case 'markets':
      return GLOSSARY.filter(t => ['VWAP', 'Order Book', 'Dark Pool', 'Limit Order'].includes(t.term));
    case 'portfolio':
      return GLOSSARY.filter(t => ['Drawdown', 'P&L', 'Sharpe Ratio', 'Alpha'].includes(t.term));
    case 'risk':
      return GLOSSARY.filter(t => ['Drawdown', 'Volatility', 'Margin Call', 'Correlation', 'Exposure'].includes(t.term));
    case 'academy':
    case 'strategy':
      return GLOSSARY.filter(t => ['Greeks', 'IV', 'Gamma', 'Theta'].includes(t.term));
    case 'simulation':
      return GLOSSARY.filter(t => ['Volatility', 'Drawdown', 'Correlation'].includes(t.term));
    case 'council':
      return GLOSSARY.filter(t => ['VWAP', 'IV', 'Greeks', 'Drawdown'].includes(t.term));
    case 'account':
      return GLOSSARY.filter(t => ['P&L', 'Margin Call', 'Stop-Loss', 'Limit Order'].includes(t.term));
    default:
      return [];
  }
};

export default function App() {
  const auth = useAuthStore();
  const isAuthenticated = !!auth.user;
  const isLoading = auth.loading;

  // Initialize auth on mount — check for existing session
  useEffect(() => { authStore.init(); }, []);

  console.log("AUTH STATE:", { isAuthenticated, isLoading, user: auth.user?.email });

  // Phase 2+3: Auth gate — NOTHING else loads until authenticated
  if (isLoading) {
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0a0a0f',color:'#6b6b80',fontFamily:'Inter,sans-serif'}}>
        <div style={{textAlign:'center'}}>
          <div style={{width:48,height:48,background:'#059669',borderRadius:14,display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:12}}>
            <span style={{color:'#fff',fontSize:18,fontWeight:900,fontStyle:'italic'}}>A</span>
          </div>
          <p style={{fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em'}}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log("UNAUTH ROUTES LOADED");
    return (
      <div style={{minHeight:'100vh',background:'#0a0a0f'}}>
        <Routes>
          <Route path="/onboarding-mockup" element={<OnboardingMockup />} />
          <Route path="/login" element={<AuthPage onAuthenticated={() => {}} />} />
          <Route path="/register" element={<AuthPage onAuthenticated={() => {}} />} />
          <Route path="/auth" element={<AuthPage onAuthenticated={() => {}} />} />
          {/* WO5: Forgot password real page */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </div>
    );
  }

  // Only render the full app after authentication
  return <AppContent />;
}

function AppContent() {
  const auth = useAuthStore();
  // Demo-user / broker-isolation: single source of truth for the
  // displayed identity. NEVER hard-code "John Doe" / "JD".
  // M-final 2026-06-05 — useUserDisplay() combines the localStorage
  // identity with /api/v1/user/portfolio's email so the top-right
  // pill renders the REAL authenticated user, not a fixture.
  const authUser = useAuthenticatedUser();
  const userDisplay = useUserDisplay();
  const location = useLocation();
  const navigate = useNavigate();
  // S28-ui-state 2026-06-12 — header chip now consumes the live
  // supervised-paper readiness so the top header reflects the actual
  // S27 gate state (Supervised Paper enabled / Live Trading Blocked)
  // instead of a stale hardcoded "Observation Mode" label.
  const { data: headerReadiness } = useSupervisedPaperReadiness();
  const headerPaperEnabled = isSupervisedPaperEnabled(headerReadiness);

  useEffect(() => {
    if (location.pathname.startsWith('/academy/module/')) {
      setActiveTab('academy');
    }
  }, [location.pathname]);

  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  // ADV-MKT-PREDEPLOY-003A: seed the initial tab from the URL so dashboard-only
  // data hooks (index bars, active-symbol chart, VIX) do not fetch on a direct
  // /markets (etc.) load before the routing effect runs. Bare '/' -> dashboard.
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const p = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
      const seg = (p.match(/^\/([a-z-]+)/) || [])[1] || '';
      if (seg === 'matrix' || seg === 'signal') return 'matrix';
      if (seg === 'intelligence-hub' || seg === 'intelligence') return 'intelligence-hub';
      if (seg === 'settings') return 'account';
      const known = ['dashboard','markets','market-pulse','portfolio','risk','academy','strategy','council','simulation','account','glossary'];
      if (known.includes(seg)) return seg;
    } catch { /* noop */ }
    return 'dashboard';
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [initialAccountTab, setInitialAccountTab] = useState('overview');
  const [tradingProfile, setTradingProfile] = useState({
    style: TRADING_STYLES[0].id,
    holdingPeriod: '1-3 Days',
    tradeFrequency: 'Moderate',
    strategyBias: 'Momentum',
    executionPreference: 'Assisted',
    positionModel: 'Volatility Adjusted'
  });
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [isDisclaimerAcknowledged, setIsDisclaimerAcknowledged] = useState(true); // Default true for safety during dev, will check localStorage
  const [isTutorOpen, setIsTutorOpen] = useState(false);
  const [chartTimeframe, setChartTimeframe] = useState('1D');
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [insightFilter, setInsightFilter] = useState('All');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ type: string; value: string; label: string; sublabel?: string; route?: string; asset_class?: string; category?: string }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    // 2026-05-15 sticky-click fix: scroll new page to top on every nav so
    // stale scroll/click state from the previous page does not bleed in.
    try { window.scrollTo(0, 0); } catch { /* SSR / test env */ }
    if (path.startsWith('/academy/module/')) { setActiveTab('academy'); return; }
    if (path.startsWith('/academy'))         { setActiveTab('academy'); setSelectedIndex(null); return; }
    if (path.startsWith('/market-pulse'))    { setActiveTab('market-pulse'); setSelectedIndex(null); return; }
    if (path.startsWith('/matrix') || path.startsWith('/signal')) { setActiveTab('matrix'); setSelectedIndex(null); return; }
    if (path.startsWith('/intelligence-hub') || path.startsWith('/intelligence')) { setActiveTab('intelligence-hub'); setSelectedIndex(null); return; }
    if (path.startsWith('/markets'))         { setActiveTab('markets'); setSelectedIndex(null); return; }
    if (path.startsWith('/portfolio'))       { setActiveTab('portfolio'); setSelectedIndex(null); return; }
    if (path.startsWith('/risk'))            { setActiveTab('risk'); setSelectedIndex(null); return; }
    if (path.startsWith('/strategy'))        { setActiveTab('strategy'); setSelectedIndex(null); return; }
    if (path.startsWith('/council'))         { setActiveTab('council'); setSelectedIndex(null); return; }
    if (path.startsWith('/simulation'))      { setActiveTab('simulation'); setSelectedIndex(null); return; }
    if (path.startsWith('/glossary'))        { setActiveTab('glossary'); setSelectedIndex(null); return; }
    if (path.startsWith('/settings'))        { setActiveTab('account'); setSelectedIndex(null); return; }
    // WO7 url parser for /account and /portfolio (2026-06-05) - handles
    // direct navigation from PortfolioModule / PortfolioSettings 'Connect
    // trading account' links. Parses ?tab= and ?connect= query params.
    if (path.startsWith('/account')) {
      setActiveTab('account');
      setSelectedIndex(null);
      try {
        const sp = new URLSearchParams(window.location.search);
        const tab = sp.get('tab');
        const connect = sp.get('connect');
        if (tab && ['overview','onboarding-preview','brokerage','api','paper-api','portfolio','risk','billing','security','ai','legal'].includes(tab)) {
          setInitialAccountTab(tab);
        }
        if (connect) {
          try { sessionStorage.setItem('adversyn_account_autoconnect', connect); } catch {}
        }
      } catch {}
      return;
    }
    if (path.startsWith('/portfolio'))       { setActiveTab('portfolio'); setSelectedIndex(null); return; }
    if (path.startsWith('/dashboard') || path === '/') {
      setActiveTab('dashboard');
    }
  }, [location.pathname]);

  // Debounced search against /api/v1/search
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const host = window.location.hostname;
        const base = apiV1Base();
        const res = await fetch(`${base}/search?q=${encodeURIComponent(q)}&limit=8`, { signal: controller.signal });
        if (!res.ok) {
          setSearchResults([]);
        } else {
          const data = await res.json();
          setSearchResults(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if ((err as any)?.name !== 'AbortError') setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 200);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [searchQuery]);

  const selectSearchResult = (item: { type: string; value: string; label: string; asset_class?: string; route?: string }) => {
    setSearchQuery('');
    setIsSearchFocused(false);
    try {
      if (item.type === 'strategy') {
        window.dispatchEvent(new CustomEvent('adversyn:navigate', { detail: { tab: 'strategy', filter: { strategy: item.value } } }));
      } else if (item.type === 'insight') {
        window.dispatchEvent(new CustomEvent('adversyn:navigate', { detail: { tab: item.route || 'dashboard', filter: { insight: item.value } } }));
      } else {
        window.dispatchEvent(new CustomEvent('adversyn:symbol-selected', { detail: item }));
      }
    } catch { /* noop */ }
  };

  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
  const [activeSymbol, setActiveSymbol] = useState<{ value: string; label: string; assetClass: 'us_equity' | 'crypto' }>({ value: 'NVDA', label: 'NVIDIA Corporation', assetClass: 'us_equity' });
  const [flashSymbol, setFlashSymbol] = useState<string | null>(null);

  // Listen for the search bar's symbol-selected event
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { type: string; value: string; label: string; asset_class?: string } | undefined;
      if (!detail || !detail.value) return;
      const ac = (detail.asset_class === 'crypto' ? 'crypto' : 'us_equity') as 'us_equity' | 'crypto';
      setActiveSymbol({ value: detail.value, label: detail.label || detail.value, assetClass: ac });
      setFlashSymbol(detail.value);
      window.setTimeout(() => setFlashSymbol(null), 1500);
      // ADV-P0K: a chart-intent symbol event routes to the Markets tab, where
      // MarketsModule opens the shared StockDetailView/RealStockChart for it.
      if ((detail as any).openChart) { try { setActiveTab('markets'); } catch { /* ignore */ } }
    };
    window.addEventListener('adversyn:symbol-selected', handler);
    return () => window.removeEventListener('adversyn:symbol-selected', handler);
  }, []);

  // ADV-P0K-direct: direct-load of ?chart=1&symbol=SYM routes to the Markets tab,
  // where MarketsModule reads the query param and opens the chart on mount.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('chart') === '1' && (u.searchParams.get('symbol') || '').trim()) {
        setActiveTab('markets');
      }
    } catch { /* ignore */ }
  }, []);

  // Real account data — drives header KPIs, Watchlist, and Risk Center positions
  const { account: defaultAccount } = useDefaultAccount();
  // WO12 header gates on viewerPortfolio (2026-06-06) — when the demo
  // user has any kind of broker connection (paper_account_connected),
  // the header must NOT fall back to defaultAccount's stale simulation
  // values. It must reflect the user's actual broker state, even when
  // that state is 'sync pending' (null balances).
  const _viewerPortfolioForHeader = useViewerPortfolio();
  // Real index proxies via Alpaca bars.
  // ADV-MKT-PREDEPLOY-003A (A2 lazy-load): the index tiles + sparklines that
  // consume these render ONLY on the Dashboard, but these hooks sit at the top
  // of App() so they previously fired on every tab. Gate the symbol (empty
  // string => useSymbolQuote no-ops) so a /markets load pulls zero index bars.
  const _idxActive = activeTab === 'dashboard';
  const spyQuote = useSymbolQuote(_idxActive ? 'SPY' : '', '1D', 'us_equity');
  const qqqQuote = useSymbolQuote(_idxActive ? 'QQQ' : '', '1D', 'us_equity');
  const diaQuote = useSymbolQuote(_idxActive ? 'DIA' : '', '1D', 'us_equity');
  const btcQuote = useSymbolQuote(_idxActive ? 'BTC/USD' : '', '1D', 'crypto');

  // Listen for cross-app navigate events (search-bar selecting a strategy or insight)
  useEffect(() => {
    const navHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tab?: string; filter?: any } | undefined;
      if (!detail || !detail.tab) return;
      const tab = detail.tab;
      const validTabs = ['dashboard', 'markets', 'market-pulse', 'portfolio', 'risk', 'academy', 'strategy', 'council', 'simulation', 'account'];
      if (validTabs.includes(tab)) {
        setActiveTab(tab);
        setSelectedIndex(null);
        if (detail.filter) {
          try { sessionStorage.setItem('adversyn:nav-filter', JSON.stringify({ tab, filter: detail.filter })); } catch { /* noop */ }
        }
      }
    };
    window.addEventListener('adversyn:navigate', navHandler);
    return () => window.removeEventListener('adversyn:navigate', navHandler);
  }, []);
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Order Executed',
      message: 'Your buy order for 100 shares of NVDA was filled at $894.30.',
      type: 'success',
      timestamp: '2 mins ago',
      read: false
    },
    {
      id: '2',
      title: 'AI Risk Alert',
      message: 'Adversyn AI detected unusual volatility in your tech holdings. Consider rebalancing.',
      type: 'ai',
      timestamp: '15 mins ago',
      read: false
    },
    {
      id: '3',
      title: 'Margin Warning',
      message: 'Your maintenance margin is approaching 80%. Please review your positions.',
      type: 'alert',
      timestamp: '1 hour ago',
      read: true
    }
  ]);

  const unreadCount = notifications.filter(n => !n.read).length;

  // ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — BUY-candidate sound-alert state + dedupe.
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('adversyn_alert_sound') !== 'off'; } catch { return true; }
  });
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('adversyn_alert_sound', next ? 'on' : 'off'); } catch { /* ignore */ }
      if (next) primeAudio(); // toggling ON is a user gesture -> satisfy autoplay policy
      return next;
    });
  };
  // BUY alert ids already chimed (persisted) + first-load guard so the existing backlog
  // never blasts audio on page load — only genuinely new crossings chime.
  const seenBuyRef = useRef<Set<string>>(new Set<string>((() => {
    try { return JSON.parse(localStorage.getItem('adversyn_buy_alert_seen') || '[]') as string[]; } catch { return []; }
  })()));
  const firstAlertLoadRef = useRef(true);

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // ADV-MKT-BUYUNBLOCK-ALERT-001 Track B — poll the breakout CROSSING alerts and
  // merge them into the EXISTING notification bell (no new banner/panel). Maps the
  // alert-only tiers onto the existing Notification shape; dedups by alert id.
  useEffect(() => {
    let cancelled = false;
    const loadBreakoutAlerts = async () => {
      try {
        const base = apiV1Base();
        const res = await fetch(`${base}/market-pulse/breakout-alerts?limit=20`);
        if (!res.ok) return;
        const json = await res.json();
        const alerts = (json?.data?.alerts || json?.alerts || []) as any[];
        if (cancelled || !Array.isArray(alerts) || alerts.length === 0) return;

        // ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — audio path: chime ONCE for a genuinely new
        // BUY CANDIDATE breakout (tier "BUY CANDIDATE breakout"). The first non-empty poll
        // seeds the seen-set WITHOUT chiming so the existing backlog never blasts on load.
        const isBuyTier = (a: any) => String(a?.tier || '').toUpperCase().includes('BUY CANDIDATE');
        const freshBuyIds = alerts
          .filter(a => a && a.id && isBuyTier(a) && !seenBuyRef.current.has(String(a.id)))
          .map(a => String(a.id));
        freshBuyIds.forEach(id => seenBuyRef.current.add(id));
        if (freshBuyIds.length) {
          try { localStorage.setItem('adversyn_buy_alert_seen', JSON.stringify(Array.from(seenBuyRef.current).slice(-200))); } catch { /* ignore */ }
        }
        if (firstAlertLoadRef.current) {
          firstAlertLoadRef.current = false; // backlog seeded; do not chime on first load
        } else if (freshBuyIds.length && soundEnabledRef.current) {
          playBuyChime(); // one chime per new-BUY batch; per-id dedupe via seenBuyRef
        }

        setNotifications(prev => {
          const have = new Set(prev.map(n => n.id));
          const mapped: Notification[] = alerts
            .filter(a => a && a.id && !have.has(String(a.id)))
            .map(a => {
              const tierU = String(a.tier || '').toUpperCase();
              const isBuy = tierU.includes('BUY CANDIDATE');
              const isBearish = tierU.includes('SHORT CANDIDATE') || tierU.includes('BEARISH');
              const px = typeof a.price === 'number' ? `$${a.price.toFixed(2)}` : a.price;
              const lvlVal = isBearish ? a.breakdown_trigger : a.breakout_trigger;
              const lvl = typeof lvlVal === 'number' ? `$${lvlVal.toFixed(2)}` : lvlVal;
              const head = a.catalyst_headline ? ` — ${a.catalyst_headline}` : '';
              const verb = isBearish ? 'broke down through' : 'crossed breakout';
              const createdAtMs = toEpochMs(a.timestamp) ?? undefined;
              return {
                id: String(a.id),
                title: `${a.tier}: ${a.symbol}`,
                // ADV-ALERTS-SYMBOL-CLICK-CHART-001 — backend-provided symbol powers the
                // clickable ticker -> chart/detail navigation. undefined => no broken link.
                symbol: String(a.symbol || '').trim().toUpperCase() || undefined,
                message: `${a.symbol} ${verb} ${lvl} (now ${px}).${head}`,
                // BUY breakout -> success(green); bearish/short + review -> alert(rose)
                type: (isBuy ? 'success' : 'alert') as Notification['type'],
                // exact ET time + relative age; live label fallback when epoch is missing.
                timestamp: (createdAtMs !== undefined ? formatAlertTime(createdAtMs, Date.now()) : 'recent'),
                createdAtMs,
                read: false,
              };
            });
          return mapped.length ? [...mapped, ...prev] : prev;
        });
      } catch {
        /* notification poll is best-effort; never disrupts the app */
      }
    };
    loadBreakoutAlerts();
    const t = setInterval(loadBreakoutAlerts, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // DEPLOY-TRACKER-001 — poll continuation-tracker alerts and merge into the
  // notification bell. Same dedup/mapping pattern as breakout alerts above.
  useEffect(() => {
    let cancelled = false;
    const loadContinuationAlerts = async () => {
      try {
        const base = apiV1Base();
        const res = await fetch(`${base}/market-pulse/continuation-alerts?limit=20`);
        if (!res.ok) return;
        const json = await res.json();
        const alerts = (json?.data?.alerts || json?.alerts || []) as any[];
        if (cancelled || !Array.isArray(alerts) || alerts.length === 0) return;

        setNotifications(prev => {
          const have = new Set(prev.map(n => n.id));
          const mapped: Notification[] = alerts
            .filter(a => a && a.id && !have.has(String(a.id)))
            .map(a => {
              const tierU = String(a.tier || '').toUpperCase();
              const isAction = Boolean(a.is_action_tier);
              const movePct = typeof a.move_since_first_pct === 'number' ? `${a.move_since_first_pct > 0 ? '+' : ''}${a.move_since_first_pct.toFixed(1)}%` : '';
              const px = typeof a.price === 'number' ? `$${a.price.toFixed(2)}` : '';
              const prom = a.prominence === 'HELD_POSITION' ? ' (you hold this)' : '';
              const createdAtMs = toEpochMs(a.timestamp) ?? undefined;
              return {
                id: String(a.id),
                title: `${a.event_type}: ${a.symbol}`,
                symbol: String(a.symbol || '').trim().toUpperCase() || undefined,
                message: `${a.symbol} continuation ${movePct} at ${px}. ${a.risk_label || tierU}${prom}`,
                type: (isAction ? 'success' : 'alert') as Notification['type'],
                timestamp: (createdAtMs !== undefined ? formatAlertTime(createdAtMs, Date.now()) : 'recent'),
                createdAtMs,
                read: false,
              };
            });
          return mapped.length ? [...mapped, ...prev] : prev;
        });
      } catch {
        /* continuation poll is best-effort */
      }
    };
    loadContinuationAlerts();
    const ct = setInterval(loadContinuationAlerts, 60_000);
    return () => { cancelled = true; clearInterval(ct); };
  }, []);

  // ADV-MKT-PREDEPLOY-003A (A2 lazy-load): the active-symbol chart renders ONLY
  // on the Dashboard. The Markets page opens its own StockDetailView/
  // RealStockChart on demand, so gate this fetch on the active tab.
  const symbolQuote = useSymbolQuote(
    activeTab === 'dashboard' ? activeSymbol.value : '',
    (chartTimeframe === '1W' || chartTimeframe === '1M' || chartTimeframe === '1Y') ? chartTimeframe : '1D',
    activeSymbol.assetClass,
  );

  const formatBarTime = (iso: string, tf: string): string => {
    try {
      const d = new Date(iso);
      if (tf === '1D') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      if (tf === '1Y') return d.toLocaleDateString([], { month: 'short', day: '2-digit' });
      return d.toLocaleDateString([], { month: 'numeric', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return iso; }
  };

  const activeChartData = useMemo(() => {
    if (symbolQuote.quote && symbolQuote.quote.bars && symbolQuote.quote.bars.length > 0) {
      return symbolQuote.quote.bars.map((b) => ({ time: formatBarTime(b.t, chartTimeframe), price: Number(b.c) || 0 }));
    }
    // Fallback to demo arrays only if a quote hasn't loaded yet AND we have no error.
    if (symbolQuote.loading) {
      switch (chartTimeframe) {
        case '1W': return DATA_1W;
        case '1M': return DATA_1M;
        case '1Y': return DATA_1Y;
        default: return DATA_1D;
      }
    }
    return [];
  }, [symbolQuote.quote, symbolQuote.loading, chartTimeframe]);

  const activeStats = symbolQuote.quote?.stats || null;
  const indexTiles = useMemo(() => {
    const fmtTile = (q: { quote: any | null }, fallbackVal: number) => {
      const stats = q.quote?.stats;
      const close = stats?.close;
      const change = stats?.change_pct;
      return {
        value: close != null ? close.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) : '—',
        change: change != null ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : '—',
        trend: (change ?? 0) >= 0 ? 'up' as const : 'down' as const,
        sparkData: (q.quote?.bars || []).slice(-12).map((b: any) => ({ value: b.c })),
      };
    };
    return {
      sp:    fmtTile(spyQuote, 0),
      ndx:   fmtTile(qqqQuote, 0),
      dow:   fmtTile(diaQuote, 0),
      btc:   fmtTile(btcQuote, 0),
    };
  }, [spyQuote.quote, qqqQuote.quote, diaQuote.quote, btcQuote.quote]);

  // VIX tile binding — pulls volatility_payload from /api/v1/market-pulse.
  // Customer-facing copy keeps it simple: the title makes the proxy
  // distinction (VIX vs VIX Proxy), the value is just the number, and the
  // sub-line is a neutral "Volatility gauge" tagline. Full diagnostic detail
  // (provider, quota, freshness ratio) is available via the API for admin
  // surfaces but stays off the customer card.
  const [vixTile, setVixTile] = useState<{
    label: string;
    value: string;
    sub: string;
    change?: string;
    trend?: 'up' | 'down';
    isProxy: boolean;
    status: string;
    tooltip: string;
  }>({
    label: 'VIX', value: 'Loading…', sub: 'Volatility gauge',
    isProxy: false, status: 'UNAVAILABLE', tooltip: '',
  });
  useEffect(() => {
    let cancelled = false;
    // ADV-MKT-PREDEPLOY-003A (A1 dedup): the VIX tile renders ONLY on the
    // Dashboard, yet this pulls the full ~1.3MB /market-pulse payload —
    // duplicating the MarketPulseTicker fetch. Gate it on the active tab so a
    // /markets load issues exactly one /market-pulse request (the ticker's).
    if (activeTab !== 'dashboard') return;
    const load = async () => {
      try {
        const base = apiV1Base();
        const res = await fetch(`${base}/market-pulse`);
        if (!res.ok) return;
        const json = await res.json();
        const d = json?.data || json || {};
        const payload = d.volatility_payload || {};
        const item = (payload.items || [])[0] || {};
        const value = item.value;
        const isProxy = !!item.is_proxy;
        const status = (item.freshness_status || payload.freshness_status || 'UNAVAILABLE').toString();
        const cp = item.change_percent;
        const label = isProxy ? 'VIX Proxy' : 'VIX';
        const tooltip = isProxy
          ? 'Using VXX as a free volatility proxy because true VIX index data is not available on the current free provider tier.'
          : 'CBOE Volatility Index (true VIX) via free-tier provider.';
        if (cancelled) return;
        if (value == null) {
          setVixTile({
            label, value: 'Unavailable', sub: 'Volatility gauge',
            isProxy, status, tooltip,
          });
          return;
        }
        const numeric = typeof value === 'number' ? value : Number(value);
        const valueStr = Number.isFinite(numeric) ? numeric.toFixed(2) : String(value);
        const changeStr =
          typeof cp === 'number' && Number.isFinite(cp)
            ? `${cp >= 0 ? '+' : ''}${cp.toFixed(2)}%`
            : undefined;
        setVixTile({
          label,
          value: valueStr,
          sub: 'Volatility gauge',
          change: changeStr,
          trend: typeof cp === 'number' && cp < 0 ? 'down' : 'up',
          isProxy,
          status,
          tooltip,
        });
      } catch {
        if (!cancelled) {
          setVixTile({
            label: 'VIX', value: 'Unavailable', sub: 'Volatility gauge',
            isProxy: false, status: 'ERROR', tooltip: '',
          });
        }
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [activeTab]);

  // 2026-06-06 h-source-of-truth-purge — Header buying power and
  // Daily P&L now come exclusively from useAccountSummary which wraps
  // /api/v1/user/portfolio. The defaultAccount fallback (which used to
  // surface sim Brain/Owl/Compass values) is gone. When the viewer has
  // no connected broker OR sync_status !== synced, the header renders
  // em-dash via the canonical formatters.
  const _sot_header = useAccountSummary();
  const _viewerHasBroker = _sot_header.brokerConnected;
  // Numeric values are derived strictly from the synced snapshot.
  const headerBuyingPower = _sot_header.syncStatus === 'synced'
    ? (_sot_header.raw.buyingPower != null ? Number(_sot_header.raw.buyingPower) : null)
    : null;
  const headerDailyPnl = _sot_header.syncStatus === 'synced'
    ? (_sot_header.raw.dailyPl != null ? Number(_sot_header.raw.dailyPl) : null)
    : null;
  const fmtAccountMoney = (v: number | null | undefined): string => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  };
  const fmtSignedPnl = (v: number | null | undefined): string => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    const sign = v >= 0 ? '+' : '';
    return sign + v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  };
  const fmtMoney = (v: number | null | undefined): string => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  };
  const fmtVolume = (v: number | null | undefined): string => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(2) + 'B';
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(2) + 'K';
    return String(v);
  };

  useEffect(() => {
    const acknowledged = localStorage.getItem('adversyn_disclaimer_acknowledged');
    if (acknowledged !== 'true') {
      setIsDisclaimerAcknowledged(false);
      setIsDisclaimerOpen(true);
    }
  }, []);

  const handleDisclaimerAgree = (data: any) => {
    console.log('Disclaimer Acknowledged:', data);
    localStorage.setItem('adversyn_disclaimer_acknowledged', 'true');
    localStorage.setItem('adversyn_disclaimer_data', JSON.stringify(data));
    setIsDisclaimerAcknowledged(true);
    setIsDisclaimerOpen(false);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('adversyn-theme') as 'light' | 'dark';
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('adversyn-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (activeTab === 'account' || activeTab === 'council') {
      setIsSidebarOpen(false);
    }
  }, [activeTab]);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [analyzingSymbol, setAnalyzingSymbol] = useState<string | null>(null);

  const handleQuickAnalyze = async (symbol: string) => {
    logInteraction('Dashboard', 'Quick Analyze', { symbol });
    setAnalyzingSymbol(symbol);
    // Simulate analysis delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    setAnalyzingSymbol(null);
    setActiveTab('council');
  };

  const toggleTheme = () => {
    logInteraction('App', 'Toggle Theme', { from: theme, to: theme === 'light' ? 'dark' : 'light' });
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const handleSidebarItemClick = (label: string, action: () => void) => {
    logInteraction('Sidebar', `Click ${label}`);
    action();
  };

  const handleLogout = () => {
    auth.logout();
    setIsProfileOpen(false);
    setActiveTab('dashboard');
    navigate('/dashboard');
  };

  // Auth gate handled by App wrapper — AppContent only renders when authenticated

  const renderContent = () => {
    if (selectedIndex) {
      return <IndexDetailPanel indexId={selectedIndex} onBack={() => setSelectedIndex(null)} />;
    }

    switch (activeTab) {
      case 'glossary':
        return <GlossaryModule />;
      case 'dashboard':
        return (
          <div className="app-container space-y-8 pb-12">
            {/* Welcome Section */}
            {/* Phase 12AA — chart-specific controls (timeframe + Study Candidate)
                moved OUT of this Welcome Section and INTO the active asset
                chart Card's headerAction (further down). They previously
                floated above the index cards which made it unclear they
                controlled the searched-asset chart. */}
            {/* UI-CLEANUP 2026-06-12 — the large supervised-paper banner was
                removed from the Dashboard main area (redundant with the
                compact header chip). Backend readiness, paper-study warnings
                and live-trading blockers are UNCHANGED; status stays on the
                header chip + Risk Center, and the Operations Command Center
                (full warnings/blockers) remains reachable via this compact
                link. The banner component was removed entirely. */}
            <div className="flex justify-end" data-testid="dashboard-ops-link-row">
              <a
                data-testid="operations-command-center-link"
                href="/dashboard/operations"
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] transition hover:text-[var(--app-text)]"
              >
                <ShieldCheck size={12} />
                Operations Command Center
              </a>
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Market Overview</h1>
                <p className="text-[var(--muted-text)] mt-1 font-medium italic">Read-only market intelligence for candidates, catalysts, and strategy study.</p>
              </div>
            </div>


            <MarketPulseDashboardContextCard />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-6">
              <Card>
                <Stat 
                  label="S&P 500 (SPY)" 
                  value={indexTiles.sp.value} 
                  change={indexTiles.sp.change} 
                  trend={indexTiles.sp.trend} 
                  onClick={() => setSelectedIndex('sp500')}
                  sparkData={[
                    { value: 5200 }, { value: 5210 }, { value: 5205 }, { value: 5220 }, { value: 5215 }, { value: 5230 }, { value: 5243 }
                  ]}
                />
              </Card>
              <Card>
                <Stat 
                  label="NASDAQ (QQQ)" 
                  value={indexTiles.ndx.value} 
                  change={indexTiles.ndx.change} 
                  trend={indexTiles.ndx.trend} 
                  onClick={() => setSelectedIndex('nasdaq')}
                  sparkData={[
                    { value: 16200 }, { value: 16250 }, { value: 16220 }, { value: 16300 }, { value: 16350 }, { value: 16400 }, { value: 16428 }
                  ]}
                />
              </Card>
              <Card>
                <Stat 
                  label="DOW (DIA)" 
                  value={indexTiles.dow.value} 
                  change={indexTiles.dow.change} 
                  trend={indexTiles.dow.trend} 
                  onClick={() => setSelectedIndex('dow')}
                  sparkData={(diaQuote.quote?.bars || []).slice(-12).map((b: any) => ({ value: b.c }))}
                />
              </Card>
              <Card>
                <div title={vixTile.tooltip}>
                  <Stat
                    label={vixTile.label}
                    value={vixTile.value}
                    change={vixTile.change}
                    trend={vixTile.trend}
                    onClick={() => setSelectedIndex('vix')}
                  />
                  <div className="mt-1 px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--muted-text)]">
                    {vixTile.sub}
                  </div>
                </div>
              </Card>
              <Card>
                <Stat 
                  label="BTC / USD" 
                  value={indexTiles.btc.value} 
                  change={indexTiles.btc.change} 
                  trend={indexTiles.btc.trend} 
                  onClick={() => setSelectedIndex('btc')}
                  sparkData={(btcQuote.quote?.bars || []).slice(-12).map((b: any) => ({ value: b.c }))}
                />
              </Card>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Chart Section */}
              <div className="lg:col-span-2 space-y-8">
                <Card
                  title={`${activeSymbol.value} / ${activeSymbol.label}`}
                  icon={TrendingUp}
                  headerAction={
                    // Phase 12AA — chart-specific controls now live INSIDE
                    // the active asset card header so it's visually clear
                    // they control THIS card's chart, not the page as a
                    // whole. On narrow screens the row wraps so controls
                    // never overflow.
                    <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3"
                         data-testid="asset-card-controls">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 mr-1">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                        LIVE
                      </div>
                      <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm">
                        {['1D', '1W', '1M', '1Y'].map((tf) => (
                          <button
                            key={tf}
                            data-testid={`dashboard-btn-timeframe-${tf.toLowerCase()}`}
                            onClick={() => {
                              if (chartTimeframe === tf) return;
                              setIsChartLoading(true);
                              setChartTimeframe(tf);
                              setTimeout(() => setIsChartLoading(false), 600);
                            }}
                            className={cn(
                              "px-3 py-1.5 rounded-lg text-[10px] font-black transition-all uppercase italic flex items-center gap-2 border",
                              chartTimeframe === tf
                                ? "bg-[var(--app-text)] text-[var(--card-bg)] shadow-[0_0_15px_rgba(var(--app-text-rgb),0.2)] border-[var(--app-text)]"
                                : "text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent"
                            )}
                          >
                            {tf}
                            {isChartLoading && chartTimeframe === tf && <Activity size={10} className="animate-pulse" />}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setActiveTab('council')}
                        data-testid="dashboard-btn-study-candidate"
                        className="flex items-center gap-2 px-3 py-2 bg-[var(--input-bg)] hover:bg-[var(--input-border)] text-[var(--app-text)] border border-[var(--input-border)] rounded-xl text-xs font-black transition-all uppercase italic"
                      >
                        <Brain size={14} />
                        Study Candidate
                      </button>
                      <button
                        onClick={() => setIsChartExpanded((v) => !v)}
                        className="p-1.5 text-[var(--muted-text)] hover:text-[var(--app-text)] transition-colors"
                        title={isChartExpanded ? "Collapse chart" : "Expand chart"}
                        data-testid="dashboard-btn-expand-chart"
                      >
                        <Maximize2 size={16} />
                      </button>
                    </div>
                  }
                >
                  <div className={cn("relative w-full overflow-hidden", isChartExpanded ? "min-h-[320px] h-[400px] lg:min-h-[640px] lg:h-[720px]" : "min-h-[240px] h-[300px] sm:min-h-[320px] sm:h-[400px]")}>
                    <AnimatePresence mode="wait">
                      {isChartLoading ? (
                        <motion.div 
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 flex items-center justify-center bg-[var(--card-bg)]/50 backdrop-blur-[2px] z-10"
                        >
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                            <span className="text-[10px] font-black text-emerald-600 uppercase italic tracking-widest">Re-calculating Alpha...</span>
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                    {!symbolQuote.loading && activeChartData.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[var(--card-bg)]/40 backdrop-blur-[1px] z-10">
                        <div className="text-center">
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">No data available</div>
                          <div className="text-xs text-[var(--muted-text)]">No bars for {activeSymbol.value} on this timeframe via the IEX feed. Try a longer timeframe.</div>
                        </div>
                      </div>
                    )}
                    {isMounted && (
                      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                        <AreaChart data={activeChartData}>
                          <defs>
                            <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--card-header-border)" vertical={false} />
                          <XAxis 
                            dataKey="time" 
                            stroke="var(--muted-text)" 
                            fontSize={10} 
                            tickLine={false} 
                            axisLine={false}
                            tick={{ fill: 'var(--muted-text)', fontWeight: 700 }}
                          />
                          <YAxis 
                            stroke="var(--muted-text)" 
                            fontSize={10} 
                            tickLine={false} 
                            axisLine={false}
                            domain={['auto', 'auto']}
                            tick={{ fill: 'var(--muted-text)', fontWeight: 700 }}
                            tickFormatter={(value) => `$${value}`}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px', boxShadow: 'var(--shadow-fintech-lg)' }}
                            itemStyle={{ color: 'var(--app-text)', fontWeight: 700 }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="price" 
                            stroke="#10b981" 
                            strokeWidth={3}
                            fillOpacity={1} 
                            fill="url(#colorPrice)" 
                            animationDuration={1500}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-[var(--card-header-border)]">
                    <div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest">Open</div>
                      <div className="text-sm font-black text-[var(--app-text)] italic">{fmtMoney(activeStats?.open)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest">High</div>
                      <div className="text-sm font-black text-[var(--app-text)] italic">{fmtMoney(activeStats?.high)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest">Low</div>
                      <div className="text-sm font-black text-[var(--app-text)] italic">{fmtMoney(activeStats?.low)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest">Vol</div>
                      <div className="text-sm font-black text-[var(--app-text)] italic">{fmtVolume(activeStats?.volume)}</div>
                    </div>
                  </div>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Card title={_sot_header.brokerConnected ? `Positions — Alpaca Paper` : 'Positions (no broker connected)'} icon={Layers} testid="dashboard-positions-card">
                    <div className="space-y-4" data-testid="dashboard-positions-list">
                      {(() => {
                        // 2026-06-06 h-source-of-truth-purge — Dashboard
                        // positions card now reads viewer.positions (from
                        // /api/v1/user/portfolio) ONLY. The legacy
                        // defaultAccount.positions path that surfaced sim
                        // Brain/Owl/Compass positions is gone, as is the
                        // WATCHLIST hardcoded fallback.
                        const viewerPositions = (_sot_header.positions || [])
                          .slice()
                          .sort((a: any, b: any) => Math.abs(parseFloat(b.market_value || '0')) - Math.abs(parseFloat(a.market_value || '0')))
                          .slice(0, 6)
                          .map((p: any) => ({
                            symbol: String(p.symbol || ''),
                            price: parseFloat(p.current_price || '0').toFixed(2),
                            change: p.change_today != null ? `${(parseFloat(p.change_today) * 100).toFixed(2)}%` : '—',
                            trend: (parseFloat(p.change_today || '0') >= 0 ? 'up' : 'down') as 'up' | 'down',
                            side: (p.side || 'long') as 'long' | 'short',
                            pnl: parseFloat(p.unrealized_pl || '0'),
                          }));
                        if (viewerPositions.length === 0) {
                          return (
                            <div data-testid="dashboard-positions-empty" className="text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] py-8 text-center">
                              {_sot_header.brokerConnected
                                ? (_sot_header.syncStatus === 'synced' ? 'No open positions' : 'Positions available after next successful sync')
                                : 'No broker connected — visit Account → Connected Brokerage to link Alpaca Paper'}
                            </div>
                          );
                        }
                        return viewerPositions.map((item: any) => (
                        <div key={item.symbol} onClick={() => window.dispatchEvent(new CustomEvent('adversyn:symbol-selected', { detail: { type: 'stock', value: item.symbol, label: item.symbol, asset_class: 'us_equity' } }))} className={cn("flex items-center justify-between p-3 rounded-xl hover:bg-[var(--input-bg)] transition-colors cursor-pointer group", flashSymbol === item.symbol && "ring-2 ring-emerald-500/60 bg-emerald-500/10")}>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[var(--input-bg)] flex items-center justify-center font-bold text-xs text-[var(--muted-text)] group-hover:bg-emerald-600 group-hover:text-white transition-all">
                              {item.symbol}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-[var(--app-text)]">{item.symbol}</div>
                              <div className="text-[10px] text-[var(--muted-text)] uppercase font-bold">NASDAQ</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-[var(--app-text)]">${item.price}</div>
                            <div className={cn(
                              "text-xs font-bold",
                              item.trend === 'up' ? "text-emerald-600" : "text-rose-600"
                            )}>
                              {item.change}
                            </div>
                          </div>
                        </div>
                      ));
                      })()}
                    </div>
                    <button className="w-full mt-6 py-2 text-xs font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest flex items-center justify-center gap-2">
                      View All Symbols <ChevronRight size={14} />
                    </button>
                  </Card>

                  <Card title="Portfolio Allocation" icon={PieChart}>
                    <div className="relative min-h-[320px] h-[320px] w-full overflow-hidden">
                      {isMounted && (
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                          <BarChart data={[
                            { name: 'Tech', value: 45, color: '#10b981' },
                            { name: 'Energy', value: 15, color: '#059669' },
                            { name: 'Fin', value: 20, color: '#f59e0b' },
                            { name: 'Health', value: 10, color: '#ef4444' },
                            { name: 'Other', value: 10, color: '#6366f1' },
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--card-header-border)" vertical={false} />
                            <XAxis dataKey="name" stroke="var(--muted-text)" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis stroke="var(--muted-text)" fontSize={10} tickLine={false} axisLine={false} />
                            <Tooltip 
                              cursor={{ fill: 'var(--input-bg)' }}
                              contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {[
                                { color: '#10b981' },
                                { color: '#059669' },
                                { color: '#f59e0b' },
                                { color: '#ef4444' },
                                { color: '#6366f1' },
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 justify-center">
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[var(--muted-text)] uppercase">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div> Tech
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[var(--muted-text)] uppercase">
                        <div className="w-2 h-2 rounded-full bg-emerald-700"></div> Energy
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-[var(--muted-text)] uppercase">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div> Fin
                      </div>
                    </div>
                  </Card>
                </div>

                <Card 
                  title="Institutional AI Insights" 
                  icon={Cpu}
                  headerAction={
                    <div className="flex items-center gap-2">
                      {['All', 'High Confidence'].map(f => (
                        <button
                          key={f}
                          onClick={() => {
                            if (insightFilter === f) return;
                            setInsightFilter(f);
                          }}
                          className={cn(
                            "px-2.5 py-1 rounded-lg text-[9px] font-black uppercase italic transition-all border",
                            insightFilter === f 
                              ? "bg-emerald-600 text-white border-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.2)]" 
                              : "bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--input-border)] hover:bg-[var(--card-border)] hover:text-[var(--app-text)]"
                          )}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { title: 'Alpha Signal', value: 'High Conviction', desc: 'Momentum divergence detected in semi-conductors.', confidence: 92 },
                      { title: 'Liquidity Map', value: 'Stable', desc: 'Institutional dark pool activity remains within normal bounds.', confidence: 74 },
                      { title: 'Volatility Regime', value: 'Expansion', desc: 'Prepare for increased range in upcoming sessions.', confidence: 88 }
                    ]
                    .filter(insight => insightFilter === 'All' || insight.confidence >= 90)
                    .map((insight, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-[var(--input-bg)] border border-[var(--input-border)] space-y-3 group hover:border-emerald-500/50 transition-all">
                        <div className="flex justify-between items-start">
                          <div className="text-[10px] font-black text-sky-500 uppercase tracking-widest">{insight.title}</div>
                          <div className="text-[9px] font-bold text-emerald-600 bg-emerald-600/10 px-1.5 py-0.5 rounded">{insight.confidence}%</div>
                        </div>
                        <div className="text-sm font-black text-[var(--app-text)] italic uppercase">{insight.value}</div>
                        <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">{insight.desc}</p>
                        <div className="pt-2">
                          <button
                            data-testid="dashboard-btn-analyze-signal"
                            onClick={() => {
                              // In a real app, this would trigger a specific analysis
                              setActiveTab('council');
                              // We can't easily pass state to the other component without a global state manager or context,
                              // but we can at least navigate there as a "feedback"
                            }}
                            className="w-full py-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl text-[9px] font-black text-[var(--muted-text)] uppercase italic hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all"
                          >
                            Analyze Signal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-[var(--card-header-border)]">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest italic">Quick Asset Analysis</h4>
                      <span className="text-[8px] font-bold text-[var(--muted-text)] uppercase tracking-tighter">Trigger AI Council Debate</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {['NVDA', 'SPY', 'BTC', 'ETH', 'TSLA'].map(s => (
                        <button
                          key={s}
                          data-testid={`dashboard-btn-analyze-${s.toLowerCase()}`}
                          onClick={() => {
                            setActiveTab('council');
                            // Ideally we'd trigger the analysis here
                          }}
                          className="px-4 py-2 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl text-xs font-black text-[var(--app-text)] uppercase italic hover:border-emerald-500 transition-all flex items-center gap-2"
                        >
                          <Zap size={12} className="text-amber-500" />
                          Analyze {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>

              {/* AI & Sidebar Section */}
              <div className="space-y-6">
                <Card title="Risk Center" icon={ShieldCheck}>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">Account Health</span>
                        <button 
                          onClick={() => {
                            setInitialAccountTab('brokerage');
                            setActiveTab('account');
                          }}
                          className="text-[9px] font-black text-emerald-600 hover:text-emerald-500 transition-colors uppercase italic border border-emerald-600/30 px-1.5 py-0.5 rounded bg-emerald-600/5"
                        >
                          Connect
                        </button>
                      </div>
                      <span className="text-xs font-black text-emerald-600 italic">EXCELLENT</span>
                    </div>
                    <div 
                      className="w-full h-2 bg-[var(--input-bg)] rounded-full overflow-hidden cursor-pointer hover:ring-1 hover:ring-emerald-500/30 transition-all"
                      onClick={() => {
                        setInitialAccountTab('brokerage');
                        setActiveTab('account');
                      }}
                    >
                      <div className="h-full bg-emerald-500 w-[92%]" />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted-text)] font-bold uppercase tracking-widest text-[10px]">Max Drawdown</span>
                        <span className="text-[var(--app-text)] font-black italic">4.2%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted-text)] font-bold uppercase tracking-widest text-[10px]">Beta Exposure</span>
                        <span className="text-[var(--app-text)] font-black italic">1.12</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--muted-text)] font-bold uppercase tracking-widest text-[10px]">Margin Usage</span>
                        <span className="text-[var(--app-text)] font-black italic">12%</span>
                      </div>
                    </div>

                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <div className="flex items-center gap-2 text-emerald-600 mb-2">
                        <Info size={14} />
                        <span className="text-xs font-black uppercase italic">AI Risk Alert</span>
                      </div>
                      <div className="relative">
                        <p className="text-xs text-[var(--muted-text)] leading-relaxed font-medium pr-12">
                          Portfolio concentration in Tech is currently 45%. Adversyn recommends hedging with SPY puts or diversifying into Energy.
                        </p>
                        <span className="absolute bottom-0 right-0 text-[8px] font-black text-[var(--muted-text)] opacity-30 uppercase tracking-tighter">v1.2.4-PRO</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card title="Market Sentiment" icon={Activity}>
                  <div className="flex items-center justify-center py-4">
                    <div className="relative w-48 h-24 overflow-hidden">
                      <div className="absolute inset-0 border-[12px] border-[var(--input-bg)] rounded-t-full"></div>
                      <div 
                        className="absolute inset-0 border-[12px] border-emerald-600 rounded-t-full transition-all duration-1000"
                        style={{ clipPath: 'inset(0 30% 0 0)' }}
                      ></div>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                        <div className="text-2xl font-black text-[var(--app-text)] italic">72</div>
                        <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Greed</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="text-center p-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)]">
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest text-opacity-50">Fear Index</div>
                      <div className="text-sm font-black text-rose-600 italic">28</div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)]">
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mb-1 tracking-widest text-opacity-50">Bullishness</div>
                      <div className="text-sm font-black text-emerald-600 italic">74%</div>
                    </div>
                  </div>
                </Card>

                <AdversynCouncil onAskCouncil={() => setActiveTab('council')} />
              </div>
            </div>
          </div>
        );
      case 'council':
        return <AICouncilAnalysisCenter />;
      case 'risk':
        return <RiskCenter />;
      case 'markets':
        return <MarketsModule />;
      case 'market-pulse':
        return <MarketPulseModule />;
      case 'intelligence-hub':
        return (
          <StrategyIntelligenceHub
            onNavigate={(tab, path) => { setActiveTab(tab as any); if (path) navigate(path); }}
            onOpenTutor={(q?: string) => { setIsTutorOpen(true); if (q) try { (window as any).__adversynPrefillTutor = q; } catch {} }}
          />
        );
      case 'matrix':
        // [phase11-v1] The Signal (formerly The Matrix) — tradable gainer radar.
        return <SignalModule />;
      case 'portfolio':
        return <PortfolioModule onOpenNotifications={() => setIsNotificationsOpen(true)} />;
      case 'strategy':
        return <StrategyLabModule />;
      case 'simulation':
        return <SimulationLabModule />;
      case 'orders':
        return <OMSModule />;
      case 'academy':
        return <AcademyModule />;
      case 'account':
        return <AccountPortal 
          initialTab={initialAccountTab} 
          onBack={() => {
            setActiveTab('dashboard');
            setIsSidebarOpen(true);
          }} 
          onLogout={handleLogout}
          tradingProfile={tradingProfile}
          onUpdateTradingProfile={setTradingProfile}
        />;
      default:
        return (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl flex items-center justify-center mx-auto shadow-sm">
                <Layers className="text-[var(--muted-text)] opacity-30" size={32} />
              </div>
              <h3 className="text-xl font-black text-[var(--muted-text)] opacity-50 uppercase italic">Module Under Construction</h3>
              <p className="text-[var(--muted-text)] text-sm font-medium">This institutional module is currently being provisioned.</p>
            </div>
          </div>
        );
    }
  };

  const getPageName = (tab: string) => {
    const names: Record<string, string> = {
      dashboard: 'Dashboard',
      markets: 'Markets',
      portfolio: 'Portfolio',
      risk: 'Risk Center',
      academy: 'Academy',
      strategy: 'Strategy Lab',
      simulation: 'Simulation Lab',
      council: 'Council Debate',
      account: 'Account Portal',
      'market-pulse': 'Market Pulse',
    };
    return names[tab] || 'Platform';
  };

  const memoizedTerms = useMemo(() => getPageTerms(activeTab), [activeTab]);

  return (
    <div className="min-h-screen transition-colors duration-300 selection:bg-emerald-500/20">
      {/* Header */}
      <header className="h-16 border-b border-[var(--header-border)] bg-[var(--header-bg)] backdrop-blur-xl fixed top-0 left-0 right-0 z-40 flex items-center gap-3 sm:gap-6 px-3 sm:px-6 transition-colors duration-300">
        {/* Mobile hamburger toggle */}
        <button
          type="button"
          className="md:hidden p-2 -ml-1 text-[var(--muted-text)] hover:text-[var(--app-text)] rounded-lg hover:bg-[var(--input-bg)] shrink-0"
          onClick={() => setIsSidebarOpen(v => !v)}
          aria-label="Toggle navigation"
          data-testid="header-mobile-hamburger"
        >
          <Menu size={20} />
        </button>
        <div 
          onClick={() => {
            setActiveTab('dashboard');
            setIsSidebarOpen(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
            setSelectedIndex(null);
            navigate('/dashboard');
          }}
          className="flex items-center gap-3 group cursor-pointer shrink-0"
        >
          <div className="relative w-10 h-10 rounded-full bg-slate-950 flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.3)] transition-all duration-500 group-hover:shadow-[0_0_30px_rgba(56,189,248,0.5)] overflow-hidden border border-slate-800">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/20 to-transparent opacity-50" />
            <Brain size={22} className="text-sky-400 fill-sky-400/20 relative z-10 drop-shadow-[0_0_8px_rgba(56,189,248,0.8)]" />
          </div>
          <div className="flex flex-col leading-none hidden sm:flex">
            <span className="text-xl font-black tracking-tight text-[var(--app-text)] uppercase italic font-display">ADVERSYN</span>
            <span className="text-xl font-black tracking-tight text-[var(--app-text)] uppercase italic font-display opacity-90">TRADING</span>
          </div>
        </div>

        {/* Primary Command Search Bar */}
        <div className="hidden md:flex flex-1 max-w-[720px] min-w-0 lg:min-w-[420px] relative shrink-0">
          <div className={cn(
            "flex items-center w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 h-10 transition-all duration-200",
            isSearchFocused && "ring-2 ring-emerald-500/20 border-emerald-500/50 bg-[var(--card-bg)]"
          )}>
            <Search size={18} className={cn("text-[var(--muted-text)] shrink-0 transition-colors", isSearchFocused && "text-emerald-500")} />
            <input
              type="text"
              value={searchQuery}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchResults.length > 0) {
                  e.preventDefault();
                  selectSearchResult(searchResults[0]);
                } else if (e.key === 'Escape') {
                  setSearchQuery('');
                  setIsSearchFocused(false);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Search symbols (e.g. AAPL, NVDA, AMD)"
              className="bg-transparent border-none outline-none text-sm w-full px-3 placeholder:text-[var(--muted-text)] placeholder:truncate font-medium text-[var(--app-text)]"
            />
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--input-bg)] border border-[var(--input-border)] text-[10px] font-bold text-[var(--muted-text)] shrink-0 ml-auto">
              <Terminal size={10} />
              <span>CMD+K</span>
            </div>
          </div>

          {/* Search Suggestions Dropdown */}
          {isSearchFocused && searchQuery.trim().length >= 2 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200 max-h-[480px] overflow-y-auto">
              <div className="p-2">
                {searchLoading && (
                  <div className="px-3 py-2 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Searching...</div>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <div className="px-3 py-2 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">{`No matches for "${searchQuery.trim()}"`}</div>
                )}
                {(['Symbols', 'Strategies', 'Insights'] as const).map((sectionLabel) => {
                  const items = searchResults.filter((r) => (r.category || 'Symbols') === sectionLabel);
                  if (items.length === 0) return null;
                  return (
                    <div key={sectionLabel} className="mb-1 last:mb-0">
                      <div className="px-3 py-2 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest border-b border-[var(--card-header-border)] mb-1">{sectionLabel}</div>
                      {items.map((item, i) => (
                        <button
                          key={`${item.type}-${item.value}-${i}`}
                          onMouseDown={(e) => { e.preventDefault(); selectSearchResult(item); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--input-bg)] rounded-lg transition-colors text-left group"
                        >
                          <div className="w-6 h-6 rounded bg-[var(--input-bg)] flex items-center justify-center group-hover:bg-emerald-500/10 transition-colors">
                            <Search size={12} className="text-[var(--muted-text)] group-hover:text-emerald-500" />
                          </div>
                          <span className="text-sm font-bold text-[var(--app-text)] shrink-0">{item.value}</span>
                          <span className="text-xs text-[var(--muted-text)] truncate flex-1">{item.label}</span>
                          {item.sublabel && <span className="text-[10px] text-[var(--muted-text)] uppercase shrink-0 ml-2">{item.sublabel}</span>}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 ml-auto shrink-0">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            data-testid="header-btn-theme-toggle"
            className="p-2 text-[var(--muted-text)] hover:text-[var(--app-text)] transition-colors rounded-xl hover:bg-[var(--input-bg)]"
            title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* S28-ui-state 2026-06-12 — header chip mirrors readiness gate.
              When supervised paper is enabled the header reads
              "Supervised Paper / Live Trading Blocked". Otherwise it falls
              back to the conservative "Observation Mode / Execution Blocked".
              Live trading remains blocked in BOTH renders. */}
          <div
            data-testid="header-execution-mode-status"
            data-paper-enabled={String(headerPaperEnabled)}
            data-live-blocked="true"
            data-execution-posture={headerReadiness?.execution_posture ?? "EXECUTION_BLOCKED"}
            className="hidden md:flex items-center gap-3 mr-4 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-xl shadow-sm transition-colors"
          >
            <div className="text-right">
              <div
                data-testid="header-execution-mode-headline"
                className="text-[10px] font-bold text-amber-300 uppercase tracking-widest leading-none mb-0.5"
              >
                {headerPaperEnabled ? "Supervised Paper" : "Observation Mode"}
              </div>
              <div
                data-testid="header-execution-mode-subline"
                className="text-xs font-black text-amber-100 uppercase italic font-mono"
              >
                {headerPaperEnabled ? "Live Trading Blocked" : "Execution Blocked"}
              </div>
            </div>
            <Lock size={14} className="text-amber-300" />
          </div>

          <div className="hidden lg:flex items-center gap-6 mr-4">
            <div className="text-right">
              <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Buying Power</div>
              <div className="text-sm font-black text-[var(--app-text)] italic">{fmtAccountMoney(headerBuyingPower)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Daily P&L</div>
              <div className="text-sm font-black text-emerald-600 italic">{fmtSignedPnl(headerDailyPnl)}</div>
            </div>
          </div>
          
          <button 
            onClick={() => {
              logInteraction('Header', 'Open Notifications');
              setIsNotificationsOpen(true);
            }}
            aria-label="Open notifications"
            data-testid="header-notifications-btn"
            className="p-2 text-[var(--muted-text)] hover:text-[var(--app-text)] transition-colors relative"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-emerald-600 rounded-full border-2 border-[var(--card-bg)]"></span>
            )}
          </button>
          
          <div className="h-8 w-[1px] bg-[var(--card-border)] mx-2"></div>
          
          <div className="relative">
            <button 
              onClick={() => {
                logInteraction('Header', 'Toggle Profile Menu');
                setIsProfileOpen(!isProfileOpen);
              }}
              className={cn(
                "flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-[var(--input-bg)] border transition-all",
                isProfileOpen ? "border-emerald-600 bg-emerald-500/10" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
              )}
            >
              <span
                data-testid="topbar-account-status"
                className="hidden sm:inline text-xs font-black text-[var(--app-text)] ml-2 uppercase italic"
              >
                {userDisplay.accountStatusLabel}
              </span>
              <div
                data-testid="topbar-account-initials"
                title={userDisplay.displayName}
                className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white font-black text-xs shadow-lg"
              >
                {userDisplay.displayInitials}
              </div>
            </button>

            <AnimatePresence>
              {isProfileOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsProfileOpen(false)} 
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-64 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-2xl z-50 overflow-hidden"
                  >
                    <div className="p-4 border-b border-[var(--card-header-border)] bg-[var(--card-header-bg)]">
                      <div className="flex items-center gap-3">
                        <div
                          data-testid="profile-chip-initials"
                          className="w-10 h-10 rounded-full bg-[var(--app-text)] flex items-center justify-center text-[var(--card-bg)] font-black italic text-sm"
                        >
                          {userDisplay.displayInitials}
                        </div>
                        <div>
                          <div
                            data-testid="profile-chip-name"
                            className="text-xs font-black text-[var(--app-text)] uppercase italic"
                          >
                            {userDisplay.displayName}
                          </div>
                          {userDisplay.email && userDisplay.source !== 'auth_email' && userDisplay.source !== 'backend_email' && (
                            <div
                              data-testid="profile-chip-email"
                              className="text-[10px] font-bold text-[var(--muted-text)] truncate max-w-[200px]"
                              title={userDisplay.email}
                            >
                              {userDisplay.email}
                            </div>
                          )}
                          <div
                            data-testid="profile-chip-status"
                            className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest"
                          >
                            {userDisplay.accountStatusLabel}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-2">
                      {[
                        { id: 'overview', label: 'Account Overview', icon: UserCircle },
                        { id: 'brokerage', label: 'Connected Brokerage', icon: Link2 },
                        { id: 'api', label: 'API Integrations', icon: Key },
                        { id: 'portfolio', label: 'Portfolio Settings', icon: BarChart3 },
                        { id: 'risk', label: 'Risk Settings', icon: ShieldCheck },
                        { id: 'billing', label: 'Billing / Subscription', icon: CreditCard },
                        { id: 'security', label: 'Security Settings', icon: Lock },
                      ].map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            logInteraction('ProfileMenu', `Navigate to ${item.label}`);
                            setInitialAccountTab(item.id);
                            setActiveTab('account');
                            setIsProfileOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[var(--muted-text)] hover:bg-[var(--input-bg)] hover:text-[var(--app-text)] transition-all group"
                        >
                          <item.icon size={16} className="text-[var(--muted-text)] group-hover:text-emerald-600" />
                          <span className="text-[10px] font-black uppercase italic tracking-tight">{item.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="p-2 border-t border-[var(--card-header-border)]">
                      <button 
                        onClick={() => {
                          logInteraction('ProfileMenu', 'Logout');
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50/10 transition-all group"
                      >
                        <LogOut size={16} />
                        <span className="text-[10px] font-black uppercase italic tracking-tight">Logout</span>
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {/* Phase 12BN sidebar: flex column so footer items never overlap nav list */}
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 top-16 bottom-10 bg-black/40 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
          data-testid="sidebar-mobile-backdrop"
          aria-hidden="true"
        />
      )}
      <aside className={cn(
        "fixed left-0 top-16 bottom-10 bg-[var(--card-bg)] border-r border-[var(--card-border)] transition-all duration-300 z-30 flex flex-col",
        "w-64",
        isSidebarOpen ? "translate-x-0 md:w-64" : "-translate-x-full md:translate-x-0 md:w-20"
      )}>
        <div className="p-4 space-y-2 flex-1 overflow-y-auto" data-testid="sidebar-main-nav">
          <SidebarItem icon={Activity} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => handleSidebarItemClick('Dashboard', () => { setActiveTab('dashboard'); setSelectedIndex(null); navigate('/dashboard'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-dashboard" />
          <SidebarItem icon={CandlestickChart} label="Markets" active={activeTab === 'markets'} onClick={() => handleSidebarItemClick('Markets', () => { setActiveTab('markets'); setSelectedIndex(null); navigate('/markets'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-markets" />
          <SidebarItem icon={BarChart3} label="Market Pulse" active={activeTab === 'market-pulse'} onClick={() => handleSidebarItemClick('Market Pulse', () => { setActiveTab('market-pulse'); setSelectedIndex(null); navigate('/market-pulse'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-market-pulse" />
          {/* [phase12cf-signal-icon-v1] The Signal — tradable gainer radar (broadcast/radar SignalIcon) */}
          <SidebarItem icon={Sparkles} label="Intelligence Hub" active={activeTab === 'intelligence-hub'} onClick={() => handleSidebarItemClick('Intelligence Hub', () => { setActiveTab('intelligence-hub'); setSelectedIndex(null); navigate('/intelligence-hub'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-intelligence-hub" />
          <SidebarItem icon={SignalIcon} label="The Signal" active={activeTab === 'matrix'} onClick={() => handleSidebarItemClick('The Signal', () => { setActiveTab('matrix'); setSelectedIndex(null); navigate('/signal'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-signal" />
          <SidebarItem icon={Briefcase} label="Portfolio" active={activeTab === 'portfolio'} onClick={() => handleSidebarItemClick('Portfolio', () => { setActiveTab('portfolio'); setSelectedIndex(null); navigate('/portfolio'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-portfolio" />
          <SidebarItem icon={ShieldAlert} label="Risk Center" active={activeTab === 'risk'} onClick={() => handleSidebarItemClick('Risk Center', () => { setActiveTab('risk'); setSelectedIndex(null); navigate('/risk'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-risk-center" />
          <SidebarItem icon={GraduationCap} label="Academy" active={activeTab === 'academy'} onClick={() => handleSidebarItemClick('Academy', () => { setActiveTab('academy'); setSelectedIndex(null); navigate('/academy'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-academy" />
          <SidebarItem icon={FlaskConical} label="Strategy Lab" active={activeTab === 'strategy'} onClick={() => handleSidebarItemClick('Strategy Lab', () => { setActiveTab('strategy'); setSelectedIndex(null); navigate('/strategy'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-strategy-lab" />
          <SidebarItem icon={Atom} label="Council Debate" active={activeTab === 'council'} onClick={() => handleSidebarItemClick('Council Debate', () => { setActiveTab('council'); setSelectedIndex(null); navigate('/council'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-council-debate" />
          <SidebarItem icon={Play} label="Simulation Lab" active={activeTab === 'simulation'} onClick={() => handleSidebarItemClick('Simulation Lab', () => { setActiveTab('simulation'); setSelectedIndex(null); navigate('/simulation'); })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-simulation-lab" />
        </div>

        <div className="border-t border-[var(--card-border)] p-4 space-y-2 shrink-0" data-testid="sidebar-footer-nav">
          <SidebarItem icon={TutorIcon} label="AI Tutor" onClick={() => handleSidebarItemClick('AI Tutor', () => setIsTutorOpen(!isTutorOpen))} collapsed={!isSidebarOpen} data-testid="sidebar-nav-ai-tutor" />
          <SidebarItem icon={SlidersHorizontal} label="Settings" active={activeTab === 'account'} onClick={() => handleSidebarItemClick('Settings', () => { 
            if (activeTab === 'account') {
              setActiveTab('dashboard');
              setIsSidebarOpen(true);
              navigate('/dashboard');
            } else {
              setActiveTab('account'); 
              setInitialAccountTab('overview'); 
              navigate('/settings');
            }
          })} collapsed={!isSidebarOpen} data-testid="sidebar-nav-settings" />
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "pt-24 pb-12 transition-all duration-300 min-h-screen flex flex-col",
        "ml-0",
        isSidebarOpen ? "md:ml-64" : "md:ml-20"
      )}>
        {['dashboard', 'markets', 'market-pulse', 'portfolio', 'risk'].includes(activeTab) && (
          <div className="mb-6">
            <MarketPulseTicker />
          </div>
        )}
        <div className="flex-grow">
          <Routes>
            <Route path="/market-pulse" element={<MarketPulsePage />} />
            {/* phase-fallen-edge-ui-v1 — standalone Fallen Edge page. */}
            <Route
              path="/fallen-edge"
              element={
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 text-gray-200">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-black uppercase italic tracking-tight font-display text-white">
                      Fallen Edge
                    </h1>
                    <p className="text-sm text-gray-500 font-medium italic">
                      Loser intelligence — reversal, continuation, and avoid lanes. Advisory-only.
                    </p>
                  </div>
                  <FallenEdgePanel />
                </div>
              }
            />
            {/* phase-short-intel-ui-v1 — standalone Short Intelligence page. */}
            <Route
              path="/short-intelligence"
              element={
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 text-gray-200">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-black uppercase italic tracking-tight font-display text-white">
                      Short Intelligence
                    </h1>
                    <p className="text-sm text-gray-500 font-medium italic">
                      Short-side research and squeeze warnings. Advisory-only.
                    </p>
                  </div>
                  <ShortIntelligencePanel />
                </div>
              }
            />
            <Route path="/academy/module/:moduleId" element={<AcademyLessonPage />} />
            <Route path="*" element={<ErrorBoundary fallbackTitle="This page hit an error">{renderContent()}</ErrorBoundary>} />
          </Routes>
        </div>

        {/* Footer - Only visible on Account/Legal pages */}
        {activeTab === 'account' && (
          <footer className="mt-20 pt-12 border-t border-[var(--card-header-border)] app-container">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-950 flex items-center justify-center border border-slate-800 shadow-[0_0_10px_rgba(56,189,248,0.2)]">
                  <Brain size={16} className="text-sky-400 fill-sky-400/10" />
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-sm font-black tracking-tight text-[var(--app-text)] uppercase italic font-display">ADVERSYN</span>
                  <span className="text-sm font-black tracking-tight text-[var(--app-text)] uppercase italic font-display opacity-80">TRADING</span>
                </div>
              </div>
              <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest leading-relaxed">
                Institutional-grade AI trading intelligence. Built for precision.
              </p>
            </div>
            
            <div>
              <h4 className="text-[10px] font-black text-[var(--app-text)] uppercase tracking-[0.2em] mb-4">Platform</h4>
              <ul className="space-y-2">
                {['Markets', 'Portfolio', 'Risk Center', 'Council'].map(item => (
                  <li key={item}>
                    <button 
                      onClick={() => {
                        logInteraction('Footer', `Navigate to ${item}`);
                        const tabMap: Record<string, string> = {
                          'Markets': 'markets',
                          'Portfolio': 'portfolio',
                          'Risk Center': 'risk',
                          'Council': 'council'
                        };
                        setActiveTab(tabMap[item] as any);
                      }}
                      className="text-[10px] font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-black text-[var(--app-text)] uppercase tracking-[0.2em] mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><button onClick={() => { logInteraction('Footer', 'Navigate to Legal'); setInitialAccountTab('legal'); setActiveTab('account'); }} className="text-[10px] font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest">Risk Disclosure</button></li>
                <li><button onClick={() => { logInteraction('Footer', 'Navigate to Legal'); setInitialAccountTab('legal'); setActiveTab('account'); }} className="text-[10px] font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest">Trading Disclaimer</button></li>
                <li><button onClick={() => logInteraction('Footer', 'Navigate to Terms')} className="text-[10px] font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest">Terms of Service</button></li>
                <li><button onClick={() => logInteraction('Footer', 'Navigate to Privacy')} className="text-[10px] font-bold text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest">Privacy Policy</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-black text-[var(--app-text)] uppercase tracking-[0.2em] mb-4">Connect</h4>
              <div className="flex gap-4">
                <button onClick={() => logInteraction('Footer', 'Connect Atom')} className="w-8 h-8 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] flex items-center justify-center text-[var(--muted-text)] hover:text-emerald-600 transition-colors"><Atom size={16} /></button>
                <button onClick={() => logInteraction('Footer', 'Connect Activity')} className="w-8 h-8 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] flex items-center justify-center text-[var(--muted-text)] hover:text-emerald-600 transition-colors"><Activity size={16} /></button>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-[var(--card-header-border)]">
            <p className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              © 2026 Adversyn Trading Systems. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-emerald-600 uppercase tracking-widest">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                System Operational
              </span>
              <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">v1.2.4-PRO</span>
            </div>
          </div>
        </footer>
        )}
      </main>

      <TradingDisclaimerModal 
        isOpen={isDisclaimerOpen} 
        onAgree={handleDisclaimerAgree} 
      />

      <AITutor 
        isOpen={isTutorOpen} 
        onClose={() => setIsTutorOpen(false)} 
      />

      <NotificationCenter 
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onClearAll={clearAll}
        soundEnabled={soundEnabled}
        onToggleSound={toggleSound}
        onSymbolClick={(symbol) => { openSymbolChart(symbol); setIsNotificationsOpen(false); }}
      />

      <PageLegend terms={memoizedTerms} />

      <SupportWidget 
        userId={auth.user?.id}
        currentPage={activeTab}
      />

      <PaperSessionPlanDrawer />
    </div>
  );
}
